import { useEffect } from "react";
import { data } from "react-router";
import Logo from "#app/components/Logo.tsx";
import { BackPreview } from "#app/components/wear/back-preview.tsx";
import {
  PreorderButton,
  PreorderPanel,
} from "#app/components/wear/preorder-panel.tsx";
import { SizeGuide } from "#app/components/wear/size-guide.tsx";
import { WordPicker } from "#app/components/wear/word-picker.tsx";
import { trackEvent } from "#app/utils/gtags.client.ts";
import { SITE_URL, organizationJsonLd } from "#app/utils/structured-data.ts";
import { rejectionMessage } from "#app/utils/wear/messages.ts";
import { paymentLinkUrl } from "#app/utils/wear/payment.ts";
import { getPreorderState } from "#app/utils/wear/state.server.ts";
import { getEligibleWords, getWearWord } from "#app/utils/wear/words.server.ts";
import { type Route } from "./+types/wear.ts";

const TITLE = "Independence 47 jersey | shonaslang™";
const DESCRIPTION =
  "Pre-order the black Independence 47 jersey from shonaslang, with the Shona slang word of your choice on the back. Only 47 made. Free UK delivery by April.";
const OG_IMAGE = `${SITE_URL}/images/wear/wear-og.png`;

export async function loader({ request }: Route.LoaderArgs) {
  const query = new URL(request.url).searchParams.get("word") ?? "";
  const { state, link } = getPreorderState();
  const [words, lookup] = await Promise.all([
    getEligibleWords(),
    query ? getWearWord(query) : null,
  ]);
  const selected = lookup && "word" in lookup ? lookup.word : null;

  return data(
    {
      state,
      query,
      words: words.map(({ slug, term }) => ({ slug, term })),
      selected: selected
        ? {
            slug: selected.slug,
            term: selected.term,
            printed: selected.printed,
            paymentUrl:
              state === "open" && link
                ? paymentLinkUrl(link, selected.reference)
                : null,
          }
        : null,
      error:
        lookup && "rejected" in lookup
          ? rejectionMessage(lookup.rejected)
          : null,
    },
    { headers: { "Cache-Control": "private, max-age=0" } },
  );
}

export const headers: Route.HeadersFunction = ({ loaderHeaders }) => ({
  "Cache-Control": loaderHeaders.get("Cache-Control") ?? "private, max-age=0",
});

// The back mockup is the page's largest image, but it sits inside an SVG, where the browser finds it late.
export const links: Route.LinksFunction = () => [
  { rel: "preload", as: "image", href: "/images/wear/back-black-blank.webp" },
];

export const meta: Route.MetaFunction = ({ data }) => [
  { title: TITLE },
  { name: "description", content: DESCRIPTION },
  ...(data?.state === "open"
    ? []
    : [{ name: "robots", content: "noindex, nofollow" }]),
  { property: "og:type", content: "product" },
  { property: "og:title", content: TITLE },
  { property: "og:description", content: DESCRIPTION },
  { property: "og:url", content: `${SITE_URL}/wear` },
  { property: "og:image", content: OG_IMAGE },
  { property: "og:image:width", content: "1200" },
  { property: "og:image:height", content: "630" },
  { name: "twitter:card", content: "summary_large_image" },
  { name: "twitter:title", content: TITLE },
  { name: "twitter:description", content: DESCRIPTION },
  { name: "twitter:image", content: OG_IMAGE },
];

function ProductJsonLd({
  state,
}: {
  state: Route.ComponentProps["loaderData"]["state"];
}) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Independence 47 jersey",
    description: DESCRIPTION,
    image: OG_IMAGE,
    brand: organizationJsonLd(),
    ...(state === "upcoming"
      ? {}
      : {
          offers: {
            "@type": "Offer",
            url: `${SITE_URL}/wear`,
            priceCurrency: "GBP",
            price: "55.00",
            availability:
              state === "open"
                ? "https://schema.org/PreOrder"
                : "https://schema.org/SoldOut",
          },
        }),
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

function FrontImage() {
  return (
    <img
      src="/images/wear/front-black.webp"
      width={736}
      height={740}
      alt="Black jersey, front: a chevron yoke in green, yellow, red and black, the shonaslang badge at the neck, ZIMBABWE and a cream 47"
      loading="lazy"
      className="block h-auto w-full"
    />
  );
}

export default function WearPage({ loaderData }: Route.ComponentProps) {
  const { state, query, words, selected, error } = loaderData;

  useEffect(() => {
    trackEvent("wear_view", {
      word: selected?.slug ?? "",
      source: query ? "word_param" : "direct",
    });
    // Once per page view, not per word change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`container py-8 ${selected ? "pb-28 lg:pb-8" : ""}`}>
      <ProductJsonLd state={state} />

      <header className="mb-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-3 text-foreground">
            <Logo size={40} trademark />
            <Logo variant="monogram" size={40} title="" />
          </div>
          <h1 className="text-h2 sm:text-h1">Independence 47</h1>
          <p className="font-sans text-body-sm text-muted-foreground">
            18.04.1980 · 47 years · 47 jerseys
          </p>
        </div>
      </header>

      <div className="grid gap-8 lg:grid-cols-2">
        <div className="space-y-4">
          <figure className="space-y-2">
            <div className="overflow-hidden rounded-lg border border-border">
              <BackPreview printed={selected?.printed} term={selected?.term} />
            </div>
            <div className="overflow-hidden rounded-lg border border-border">
              <FrontImage />
            </div>
            <figcaption className="font-sans text-body-2xs text-muted-foreground">
              Render: final product may differ. Sample photos will replace these
              images.
            </figcaption>
          </figure>
        </div>

        <div className="space-y-8">
          <WordPicker
            words={words}
            value={selected?.term ?? query}
            error={error ?? undefined}
          />
          <PreorderPanel
            state={state}
            word={selected ?? undefined}
            paymentUrl={selected?.paymentUrl}
          />
          <section aria-labelledby="edition-heading" className="space-y-3">
            <h2 id="edition-heading" className="text-h5">
              What the 47 means
            </h2>
            <p className="text-body-sm">
              Zimbabwe became independent on 18 April 1980. This jersey marks 47
              years, and only 47 will be made.
            </p>
            <p className="text-body-sm">
              Your word goes where a player's name would be, above the 47,
              printed in cream on the black nameplate. Choose any word from the
              dictionary with 12 letters or fewer.
            </p>
          </section>
          <SizeGuide />
        </div>
      </div>

      {selected ? (
        <div className="fixed inset-x-0 bottom-0 z-10 border-t border-border bg-background p-4 lg:hidden">
          <PreorderButton
            state={state}
            word={selected}
            paymentUrl={selected.paymentUrl}
          />
        </div>
      ) : null}
    </div>
  );
}
